<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <!-- Loading -->
    <div v-if="loading" class="d-flex justify-center align-center py-12">
      <v-progress-circular indeterminate color="accent" size="36" />
    </div>

    <template v-else>
      <!-- Current status -->
      <v-row class="mb-3">
        <v-col cols="12" sm="6">
          <v-sheet outlined rounded class="pa-5 text-center" height="100%">
            <div
              class="caption text--secondary mb-1 text-uppercase letter-spacing-1"
            >
              Student Payments Right Now
            </div>
            <div
              class="text-h4 font-weight-bold"
              :class="isOpen ? 'success--text' : 'error--text'"
            >
              {{ isOpen ? "Open" : "Closed" }}
            </div>
            <div class="caption grey--text mt-1">
              Pay Now button on the student portal
            </div>
          </v-sheet>
        </v-col>

        <v-col cols="12" sm="6">
          <v-sheet outlined rounded class="pa-5 text-center" height="100%">
            <div
              class="caption text--secondary mb-1 text-uppercase letter-spacing-1"
            >
              Current Setting
            </div>
            <div class="text-h6 font-weight-bold accent--text">
              {{ modeLabel(setting && setting.mode) }}
            </div>
            <div class="caption grey--text mt-1">
              {{ windowText(setting) }}
            </div>
          </v-sheet>
        </v-col>
      </v-row>

      <!-- Change setting -->
      <v-sheet outlined rounded class="pa-5 mb-5">
        <div
          class="subtitle-2 font-weight-semibold mb-3 grey--text text--darken-2"
        >
          Change Setting
        </div>

        <v-form ref="form" v-model="valid" @submit.prevent="save">
          <!-- Option cards -->
          <v-item-group v-model="form.mode" mandatory>
            <v-row dense class="mb-2">
              <v-col
                v-for="option in modeOptions"
                :key="option.value"
                cols="12"
                sm="4"
              >
                <v-item v-slot="{ active, toggle }" :value="option.value">
                  <v-card
                    outlined
                    class="pa-4 option-card"
                    height="100%"
                    :class="{ 'option-card--active': active }"
                    :style="active ? { borderColor: option.hex } : {}"
                    @click="toggle"
                  >
                    <div class="d-flex align-center mb-2">
                      <v-icon
                        :color="active ? option.color : 'grey'"
                        class="mr-2"
                      >
                        {{ option.icon }}
                      </v-icon>
                      <span class="subtitle-2 font-weight-bold">
                        {{ option.title }}
                      </span>
                      <v-spacer />
                      <v-icon v-if="active" small :color="option.color">
                        mdi-check-circle
                      </v-icon>
                    </div>
                    <div class="caption grey--text text--darken-1">
                      {{ option.description }}
                    </div>
                  </v-card>
                </v-item>
              </v-col>
            </v-row>
          </v-item-group>

          <v-row dense>
            <template v-if="form.mode === 'scheduled'">
              <v-col cols="12" sm="4">
                <DateTimePicker
                  v-model="form.disable_from"
                  label="Disable from *"
                  :rules="[(v) => !!v || 'Start date and time is required']"
                  :error-messages="errors.disable_from"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <DateTimePicker
                  v-model="form.disable_until"
                  label="Enable again at"
                  clearable
                  hint="Leave empty to keep payments closed until changed"
                  :min="(form.disable_from || '').split('T')[0] || today"
                  :error-messages="errors.disable_until"
                />
              </v-col>
            </template>
          </v-row>

          <v-row dense>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="form.remarks"
                label="Remarks (optional)"
                outlined
                dense
                counter="500"
                :rules="[(v) => !v || v.length <= 500 || 'Max 500 characters']"
              />
            </v-col>
          </v-row>

          <!-- Plain-language summary of what will happen on save -->
          <v-alert
            dense
            text
            :type="form.mode === 'disabled' ? 'warning' : 'info'"
            class="caption mb-3"
          >
            {{ previewText }}
          </v-alert>

          <div class="caption grey--text mb-3">
            Times are in your local time (IST).
          </div>

          <v-btn
            small
            class="accent white--text"
            type="submit"
            :loading="saving"
            :disabled="!valid"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-form>
      </v-sheet>

      <!-- History -->
      <template v-if="setting && setting.history && setting.history.length">
        <div
          class="subtitle-2 font-weight-semibold mb-3 grey--text text--darken-2"
        >
          Change History
        </div>

        <v-sheet outlined rounded class="pa-0 overflow-hidden mb-2">
          <v-simple-table dense>
            <thead>
              <tr>
                <th class="caption">#</th>
                <th class="caption">Setting</th>
                <th class="caption">Window</th>
                <th class="caption">Remarks</th>
                <th class="caption">Set By</th>
                <th class="caption">Set At</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(entry, i) in visibleHistory"
                :key="i"
                :class="i % 2 === 0 ? '' : 'grey lighten-5'"
              >
                <td class="caption grey--text">{{ i + 1 }}</td>
                <td class="caption">{{ modeLabel(entry.mode) }}</td>
                <td class="caption">{{ windowText(entry) }}</td>
                <td class="caption">{{ entry.remarks || "—" }}</td>
                <td class="caption">{{ entry.updated_by || "—" }}</td>
                <td class="caption grey--text">
                  {{ formatDateTime(entry.updated_at) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-sheet>

        <div
          v-if="setting.history.length > historyPageSize"
          class="text-center mt-1 mb-4"
        >
          <v-btn
            text
            x-small
            color="accent"
            @click="showAllHistory = !showAllHistory"
          >
            {{
              showAllHistory
                ? "Show less"
                : `Show ${setting.history.length - historyPageSize} more…`
            }}
          </v-btn>
        </div>
      </template>
    </template>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import DateTimePicker from "@/components/DateTimePicker.vue";

export default {
  components: { DateTimePicker },

  data() {
    return {
      setting: null,
      loading: false,
      saving: false,
      valid: true,
      form: {
        mode: "enabled",
        disable_from: "",
        disable_until: "",
        remarks: "",
      },
      errors: {},
      showAllHistory: false,
      historyPageSize: 5,
      now: new Date(),
      clock: null,
      snackbar: { show: false, text: "", color: "success" },
      modeOptions: [
        {
          value: "enabled",
          title: "Enable Now",
          icon: "mdi-play-circle-outline",
          color: "success",
          hex: "#4caf50",
          description:
            "Students can pay right away. Optionally pick a time to close payments automatically.",
        },
        {
          value: "disabled",
          title: "Disable Now",
          icon: "mdi-stop-circle-outline",
          color: "error",
          hex: "#ff5252",
          description:
            "Pay Now is hidden for all students until you enable it again.",
        },
        {
          value: "scheduled",
          title: "Disable for a Period",
          icon: "mdi-calendar-clock",
          color: "accent",
          hex: "#1976d2",
          description:
            "Payments stay open, but close between the dates you choose (e.g. during maintenance).",
        },
      ],
    };
  },

  computed: {
    // "YYYY-MM-DD" in local time, the earliest date the pickers allow
    today() {
      return this.toLocalInput(this.now.toISOString()).split("T")[0];
    },

    previewText() {
      const f = this.form;
      if (f.mode === "disabled") {
        return "Students will not be able to pay online until you enable payments again.";
      }
      if (f.mode === "scheduled") {
        if (!f.disable_from) return "Pick when payments should be disabled.";
        const reopen = f.disable_until
          ? `and open again at ${this.formatDateTime(f.disable_until)}`
          : "and stay closed until you change this setting";
        return `Students can pay as usual. Payments will close at ${this.formatDateTime(
          f.disable_from
        )} ${reopen}.`;
      }
      return f.disable_from
        ? `Students will be able to pay immediately, until ${this.formatDateTime(
            f.disable_from
          )}, then payments close automatically.`
        : "Students will be able to pay immediately, until you change this setting.";
    },

    // Worked out in the browser too, so "Open/Closed" flips at the scheduled time
    // without reloading the page.
    isOpen() {
      const s = this.setting;
      if (!s) return true;
      if (s.mode === "disabled") return false;
      const from = s.disable_from ? new Date(s.disable_from) : null;
      if (s.mode === "enabled") return !from || this.now < from;
      // scheduled: closed only inside the window
      const until = s.disable_until ? new Date(s.disable_until) : null;
      const inWindow = this.now >= from && (!until || this.now <= until);
      return !inWindow;
    },

    visibleHistory() {
      if (!this.setting?.history) return [];
      return this.showAllHistory
        ? this.setting.history
        : this.setting.history.slice(0, this.historyPageSize);
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "BillDesk Payments" }]);
    this.fetchSetting();
    this.clock = setInterval(() => (this.now = new Date()), 30 * 1000);
  },

  beforeDestroy() {
    clearInterval(this.clock);
  },

  methods: {
    async fetchSetting() {
      this.loading = true;
      try {
        const res = await this.$axios.get("/billdesk_payment_setting");
        this.setting = res.data.data;
        this.resetForm();
      } catch (e) {
        console.log(e);
        this.setting = null;
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      const s = this.setting || {};
      this.form = {
        mode: s.mode || "enabled",
        disable_from: this.toLocalInput(s.disable_from),
        disable_until: this.toLocalInput(s.disable_until),
        remarks: "",
      };
      this.errors = {};
    },

    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      this.errors = {};
      try {
        const res = await this.$axios.post("/billdesk_payment_setting", {
          billdesk_payment_setting: {
            mode: this.form.mode,
            disable_from:
              this.form.mode === "disabled"
                ? null
                : this.toIso(this.form.disable_from),
            disable_until:
              this.form.mode === "scheduled"
                ? this.toIso(this.form.disable_until)
                : null,
            remarks: this.form.remarks || null,
          },
        });
        this.setting = res.data.data;
        this.now = new Date();
        this.resetForm();
        this.notify("BillDesk payment setting saved", "success");
      } catch (e) {
        this.errors = e?.response?.data?.errors || {};
        this.notify("Could not save the setting", "error");
      } finally {
        this.saving = false;
      }
    },

    notify(text, color) {
      this.snackbar = { show: true, text, color };
    },

    modeLabel(mode) {
      return (
        {
          enabled: "Enabled",
          disabled: "Disabled",
          scheduled: "Disabled for a Period",
        }[mode] || "—"
      );
    },

    windowText(s) {
      if (!s || s.mode === "disabled") return "Closed until changed";
      if (s.mode === "scheduled") {
        const until = s.disable_until
          ? `to ${this.formatDateTime(s.disable_until)}`
          : "onwards (until changed)";
        return `Closed from ${this.formatDateTime(s.disable_from)} ${until}`;
      }
      return s.disable_from
        ? `Open until ${this.formatDateTime(s.disable_from)}`
        : "Open until changed";
    },

    // "2026-10-26T14:30:00Z" -> "2026-10-26T20:00" (local time) for the picker
    toLocalInput(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate()
      )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    // picker value (browser local time) -> UTC ISO string for the API
    toIso(local) {
      return local ? new Date(local).toISOString() : null;
    },

    // -> "27-10-2026 22:00" (local time)
    formatDateTime(iso) {
      if (!iso) return "—";
      const [date, time] = this.toLocalInput(iso).split("T");
      const [y, m, d] = date.split("-");
      return `${d}-${m}-${y} ${time}`;
    },
  },
};
</script>

<style scoped>
.option-card {
  cursor: pointer;
  border-width: 2px !important;
  transition: border-color 0.15s, background-color 0.15s;
}
.option-card:hover {
  background-color: #fafafa;
}
.option-card--active {
  background-color: #f5f9ff;
}
</style>
